import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, Menu, X, Phone, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Toaster } from "sonner";
import WhatsAppButton from "@/components/WhatsAppButton";
import AnnouncementBar from "@/components/AnnouncementBar";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/katalog?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <AnnouncementBar/>
      {/* Top bar */}
      <div className="bg-ink-900 text-white text-xs">
        <div className="kz-container flex items-center justify-between py-2">
          <div className="flex items-center gap-4 opacity-90">
            <span className="hidden sm:flex items-center gap-1.5"><Phone size={12}/> 0 553 942 67 89</span>
            <span className="hidden md:flex items-center gap-1.5"><Mail size={12}/> admin@kuzeyyapi.tr</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline opacity-75">Türkiye'nin profesyonel hırdavat tedarikçisi</span>
            <Link data-testid="topbar-b2b-link" to="/bayi-basvuru" className="text-brand font-semibold hover:underline">Bayi Başvurusu</Link>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-ink-200">
        <div className="kz-container flex items-center gap-6 py-4">
          <Link data-testid="header-logo" to="/" className="flex items-center gap-3 shrink-0">
            <img src="https://customer-assets.emergentagent.com/job_kuzey-b2b-hub/artifacts/ngl4j0hv_kuzey%20%20yap%C4%B1%20logo.jpg"
                 alt="Kuzey Yapı Hırdavat" className="w-11 h-11 object-cover rounded-sm shadow-sm"/>
            <div className="leading-none">
              <div className="font-display font-black text-xl tracking-tight">KUZEY YAPI</div>
              <div className="text-[10px] tracking-[0.2em] text-ink-500 font-semibold">HIRDAVAT & İNŞAAT</div>
            </div>
          </Link>

          <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-2xl">
            <div className="flex w-full border border-ink-200 focus-within:border-ink-900 transition-colors">
              <input
                data-testid="search-input"
                value={q} onChange={(e)=>setQ(e.target.value)}
                placeholder="Ürün, marka veya SKU ara..."
                className="flex-1 px-4 py-2.5 outline-none text-sm bg-white"
              />
              <button data-testid="search-submit" type="submit" className="bg-ink-900 text-white px-5 hover:bg-brand transition-colors">
                <Search size={18}/>
              </button>
            </div>
          </form>

          <nav className="hidden lg:flex items-center gap-5 text-sm font-medium">
            <NavLink data-testid="nav-catalog" to="/katalog" className={({isActive}) => isActive ? "text-brand" : "text-ink-700 hover:text-ink-900"}>Katalog</NavLink>
            <NavLink data-testid="nav-about" to="/hakkimizda" className={({isActive}) => isActive ? "text-brand" : "text-ink-700 hover:text-ink-900"}>Hakkımızda</NavLink>
            <NavLink data-testid="nav-contact" to="/iletisim" className={({isActive}) => isActive ? "text-brand" : "text-ink-700 hover:text-ink-900"}>İletişim</NavLink>
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <div className="hidden sm:flex items-center gap-2">
                {user.role === "admin" && (
                  <Link data-testid="nav-admin" to="/admin" className="text-xs font-bold bg-ink-900 text-white px-3 py-2 rounded-sm">ADMIN</Link>
                )}
                {user.role === "b2b_dealer" && (
                  <Link data-testid="nav-b2b" to="/bayi-paneli" className="text-xs font-bold bg-b2b text-white px-3 py-2 rounded-sm">BAYİ PANELİ</Link>
                )}
                <Link data-testid="nav-account" to="/hesabim" className="flex items-center gap-1.5 text-sm font-medium text-ink-700 hover:text-ink-900">
                  <User size={18}/>
                  <span className="hidden sm:inline">{user.full_name.split(" ")[0]}</span>
                </Link>
                <button data-testid="logout-btn" onClick={logout} className="text-xs text-ink-500 hover:text-ink-900">Çıkış</button>
              </div>
            ) : (
              <Link data-testid="nav-login" to="/giris" className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-ink-700 hover:text-ink-900">
                <User size={18}/> Giriş
              </Link>
            )}
            <Link data-testid="nav-cart" to="/sepet" className="relative p-2 hover:bg-ink-100 transition-colors">
              <ShoppingCart size={22}/>
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-brand text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{count}</span>
              )}
            </Link>
            <button data-testid="menu-toggle" className="lg:hidden p-2" onClick={()=>setOpen(!open)}>
              {open ? <X size={22}/> : <Menu size={22}/>}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-ink-200 bg-white">
            <div className="kz-container py-3 flex flex-col gap-2">
              <form onSubmit={onSearch} className="md:hidden flex border border-ink-200 mb-2">
                <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Ara..." className="flex-1 px-3 py-2 outline-none text-sm"/>
                <button type="submit" className="bg-ink-900 text-white px-4"><Search size={16}/></button>
              </form>
              <NavLink to="/katalog" onClick={()=>setOpen(false)} className="py-2 font-medium">Katalog</NavLink>
              <NavLink to="/hakkimizda" onClick={()=>setOpen(false)} className="py-2 font-medium">Hakkımızda</NavLink>
              <NavLink to="/iletisim" onClick={()=>setOpen(false)} className="py-2 font-medium">İletişim</NavLink>
              {!user && <NavLink to="/giris" onClick={()=>setOpen(false)} className="py-2 font-medium">Giriş</NavLink>}
              {user && <button onClick={()=>{logout();setOpen(false);}} className="py-2 font-medium text-left">Çıkış</button>}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-ink-900 text-white mt-16">
        <div className="kz-container py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img src="https://customer-assets.emergentagent.com/job_kuzey-b2b-hub/artifacts/ngl4j0hv_kuzey%20%20yap%C4%B1%20logo.jpg"
                   alt="Kuzey Yapı" className="w-12 h-12 object-cover rounded-sm"/>
              <div className="font-display font-black text-xl">KUZEY YAPI</div>
            </div>
            <p className="text-sm text-white/60">Profesyoneller için kaliteli hırdavat ve inşaat malzemeleri. 1998'den beri güvenle.</p>
          </div>
          <div>
            <h4 className="font-display font-bold mb-3 text-sm tracking-wider">KATEGORİLER</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/katalog?kategori=elektrikli-aletler" className="hover:text-brand">Elektrikli Aletler</Link></li>
              <li><Link to="/katalog?kategori=el-aletleri" className="hover:text-brand">El Aletleri</Link></li>
              <li><Link to="/katalog?kategori=hirdavat" className="hover:text-brand">Hırdavat</Link></li>
              <li><Link to="/katalog?kategori=boya" className="hover:text-brand">Boya</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display font-bold mb-3 text-sm tracking-wider">KURUMSAL</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/hakkimizda" className="hover:text-brand">Hakkımızda</Link></li>
              <li><Link to="/bayi-basvuru" className="hover:text-brand">Bayi Başvurusu</Link></li>
              <li><Link to="/iletisim" className="hover:text-brand">İletişim</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display font-bold mb-3 text-sm tracking-wider">İLETİŞİM</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li className="flex items-center gap-2"><Phone size={14}/> 0 553 942 67 89</li>
              <li className="flex items-center gap-2"><Mail size={14}/> admin@kuzeyyapi.tr</li>
              <li className="mt-3 text-white/50 text-xs">Yeni Mahalle<br/>Oktay-Olcay Yurtbay Cad. No:28/A<br/>Çaycuma / ZONGULDAK</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="kz-container py-4 text-xs text-white/50 flex items-center justify-between">
            <span>© 2026 Kuzey Yapı Hırdavat. Tüm hakları saklıdır.</span>
            <span>Profesyoneller için inşa edildi.</span>
          </div>
        </div>
      </footer>

      <Toaster position="top-right" richColors/>
      <WhatsAppButton/>
    </div>
  );
}
